// SLEEPSAKE: re-applies an email's discount code after Shopify's market
// redirect (DEV-853). An email link applies its code on the store it opens
// (`/discount/CODE`), but Shopify then sends a visitor from another country to
// their own store (sleepsake.com.au, .co.uk, .com), which keeps its own cart
// and has never seen the code: the visitor lands at full price. So the links
// also carry the code in the page address they redirect to (`?discount=CODE`),
// which Shopify keeps through its redirect, and this applies it on whichever
// store the visitor ends up on. Shopify itself ignores `?discount=` on a page.

const PARAM = "discount";
const CODE = /^[A-Za-z0-9_-]{3,64}$/;

/**
 * The carried code this store still needs, or null: none carried, not a code
 * Shopify could issue, or this store already holds it (the link opened the
 * visitor's own store, so Shopify applied it before the redirect).
 *
 * @param {string} search - `location.search`
 * @param {string} cookie - `document.cookie`
 * @returns {string | null}
 */
export function carriedCode(search, cookie) {
  const code = new URLSearchParams(search).get(PARAM);
  if (!code || !CODE.test(code)) return null;
  const held = cookie.split(/;\s*/).includes(`discount_code=${code}`);
  return held ? null : code;
}

/**
 * The address without the carried code, so a reload or a shared link does not
 * carry it again. Only the `discount` pairs go; every other parameter keeps
 * the exact characters Shopify and Klaviyo wrote, and the hash stays.
 *
 * @param {string} href - `location.href`
 * @returns {string} a path, for `history.replaceState`
 */
export function withoutCarry(href) {
  const url = new URL(href);
  const kept = url.search
    .slice(1)
    .split("&")
    .filter((pair) => pair && pairName(pair) !== PARAM);
  return url.pathname + (kept.length ? `?${kept.join("&")}` : "") + url.hash;
}

/** @param {string} pair */
function pairName(pair) {
  try {
    return decodeURIComponent(pair.split("=")[0].replace(/\+/g, " "));
  } catch {
    return pair;
  }
}

/**
 * Tells the cart drawer the cart changed, so it redraws with the code. The
 * drawer is drawn by the server before the code was applied, and its code
 * field rebuilds the cart's codes from what it shows: left stale, it would show
 * full price and drop the carried code when the visitor types another.
 *
 * @param {Window} win
 * @param {object} cart - the `/cart.js` cart
 */
async function redrawCart(win, cart) {
  // Dynamic so the module stays importable under plain Node for the tests: a
  // static `@theme/events` specifier only resolves under the storefront importmap.
  const { DiscountUpdateEvent } = await import("@theme/events");
  win.document.dispatchEvent(new DiscountUpdateEvent(cart, "discount-carry"));
}

/**
 * Applies the carried code the way an email link does, through this store's
 * `/discount/` route, whose redirect is pointed at the cart JSON rather than a
 * page, then redraws the cart. Never throws: a failure leaves the visitor where
 * they were.
 *
 * @param {Window} win
 * @param {(win: Window, cart: object) => Promise<void>} [onApplied] - redraws the cart
 */
export async function applyCarriedCode(win, onApplied = redrawCart) {
  const { location, history } = win;
  if (!new URLSearchParams(location.search).has(PARAM)) return;
  const code = carriedCode(location.search, win.document.cookie);
  try {
    if (code) {
      const response = await win.fetch(`/discount/${encodeURIComponent(code)}?redirect=%2Fcart.js`, {
        credentials: "same-origin",
      });
      if (response.ok) await onApplied(win, await response.json());
    }
  } catch {
    // Offline or blocked: the code is still in the email to type in.
  }
  history.replaceState(history.state, "", withoutCarry(location.href));
}

if (typeof window !== "undefined") applyCarriedCode(window);
